// Summary-view registry.
//
// Modeled on `charts/registry.tsx`, and persisted the same way: a chart view is
// remembered per feature (`charts/useChartPrefs`), a summary view per config
// (`useSummaryCardPrefs`). This module is the seam that lets a single summary
// `kind` expose an ordered list of switchable views — and it stays the authority
// on both what a config may show and which period granularities it may offer
// (`viewsForConfig` / `windowsForConfig`), so a remembered view or span the
// config can't currently offer is dropped rather than applied.
//
// Stage 1 is pure data + types: no JSX view bodies yet. Those land in
// Stage 4, when a `SUMMARY_VIEW_BODIES: Record<SummaryViewId, React.FC<
// SummaryViewBodyProps>>` map is added here (mirroring `CHART_BODIES`).
//
// Two maps drive the card:
//   * SUMMARY_VIEWS         — kind → ordered list of view ids. The first
//     entry is the default (see `defaultViewFor`).
//   * SUMMARY_KIND_WINDOWS  — kind → allowed picker granularities. Most
//     kinds allow all five; `period_over_period` drops the nonsensical
//     'All' and '6M' windows.
//
// Both are `Record<SummaryKind, …>`, so the compiler flags any kind that
// is missing an entry.

import React from 'react';
import type { SummaryKind, SummaryNumericResponse, SummaryConfig } from '../../api/client';
import type { Granularity, DateWindow } from '../../charts/windowRange';
import { GRANULARITIES } from '../../charts/windowRange';
import { CHART_ICONS } from '../../charts/icons';
import StatsSummaryView from './views/StatsSummaryView';
import LineSummaryView from './views/LineSummaryView';
import TimeseriesSummaryView from './views/TimeseriesSummaryView';
import DistributionSummaryView from './views/DistributionSummaryView';
import CalendarSummaryView from './views/CalendarSummaryView';
import CoverageTrendView from './views/CoverageTrendView';

// The set of concrete view bodies a summary card can render. Extend this
// union as new bodies are written, then append their id to the relevant
// `SUMMARY_VIEWS` cycles.
//
//   stats        — count/sum/avg/min/max table (and the period/period
//                  comparison panel). Default for every kind.
//   line         — period_over_period current-vs-prior comparison line.
//   timeseries   — plain value-over-time "Trend" line for one numeric feature
//                  (the summary analogue of the standalone LineChartBody).
//   distribution — value histogram ("Distribution"), the summary analogue of
//                  HistogramBody (shares the HistogramChart core).
//   calendar     — month heatmap of logged days ("Calendar"), the consistency
//                  companion to the streaks stats (shares ActivityCalendar).
//   coverage-trend — line of the logging_rate coverage % over time, binned by
//                  month (or year for monthly cadence).
export type SummaryViewId =
  | 'stats' | 'line' | 'timeseries' | 'distribution' | 'calendar' | 'coverage-trend';

// What backend data each view body consumes. The card is the single fetch
// owner: it reads this and supplies exactly the declared sources, then hands
// them down — no view body fetches its own data.
//   numeric      — the live summary payload (always available; also carries the
//                  feature_id the card fetches the series for).
//   timeseries   — the raw feature series (fetched on demand by the card).
//   active-dates — the populated-day list the card already fetches for paging.
// The `Record<SummaryViewId, …>` shape flags any view that forgot to declare
// its sources.
export type SummarySource = 'numeric' | 'timeseries' | 'active-dates';

export const SUMMARY_VIEW_SOURCES: Record<SummaryViewId, SummarySource[]> = {
  stats: ['numeric'],
  line: ['numeric', 'timeseries'],
  // 'numeric' carries the feature_id the card fetches the series for; the raw
  // series itself is 'timeseries'.
  timeseries: ['numeric', 'timeseries'],
  distribution: ['numeric', 'timeseries'],
  calendar: ['active-dates'],
  'coverage-trend': ['active-dates'],
};

// Props every view body receives. Mirrors the chart `ChartBodyProps`
// contract — a body is a stateless visualization with no card chrome,
// header, or picker of its own; the card mounts it inside its chrome and
// drives the active window.
//
// `window`/`granularity` describe the currently-selected period (the same
// window the card's picker resolves). A line body, for instance, filters
// the feature's raw timeseries to `[window.start, window.end)` and labels
// the axis by `granularity`. Kept minimal but forward-looking: this is the
// contract Stages 4-5 depend on, so widening it later is a breaking change
// to those bodies.
export interface SummaryViewBodyProps {
  /** The live numeric panel payload for the selected window. */
  numeric: SummaryNumericResponse;
  /** The persisted config that produced this card. */
  config: SummaryConfig;
  /** Friendly feature name for titles, when the card targets one feature. */
  featureName?: string;
  /** The feature's unit, when known. */
  unit?: string;
  /** Active picker granularity (W/M/6M/Y/All). */
  granularity: Granularity;
  /** Active period bounds in ms ([start, end), half-open). */
  window: DateWindow;
  /** Raw feature timeseries (ms-dated), supplied by the card only for views
   *  whose `SUMMARY_VIEW_SOURCES` includes 'timeseries'; `[]` otherwise. The
   *  body slices it to the active period itself. */
  timeseries?: Array<{ date: number; value: number }>;
  /** The days this config's numbers count (ms, local midnight) — `/active-dates`
   *  `dates`, the 'active-dates' source. Always supplied by the card. */
  activeDates?: number[];
  /** Every day with data for this config (`paging_dates`) — the same list as
   *  `activeDates`, except on a yes/no streak or logging rate, where it also
   *  holds the answered days that weren't counted. An unbounded ('All') window
   *  spans these days, so it starts where the card's numbers start: on the
   *  first answer, not the first counted one. Always supplied by the card. */
  pagingDates?: number[];
}

// kind → ordered view cycle. First entry is the default. Every existing
// kind is seeded with the single legacy stats view so the seam is ready
// but behavior is unchanged; `period_over_period` additionally exposes the
// line view (wired in Stage 4/5).
export const SUMMARY_VIEWS: Record<SummaryKind, SummaryViewId[]> = {
  single_feature_stats: ['stats', 'timeseries', 'distribution'],
  stratified_single: ['stats'],
  streaks: ['calendar', 'stats'],
  // The calendar (a % banner over a green=logged / gray=missed heatmap) is the
  // headline and default; the trend line shows that % over time. No plain stats
  // tab — the calendar's banner already carries the number.
  logging_rate: ['calendar', 'coverage-trend'],
  period_over_period: ['line', 'stats'],
  outliers: ['stats'],
  project_narrative: ['stats'],
  paired_same_day: ['stats'],
  paired_lagged: ['stats'],
  threshold_cross: ['stats'],
  rolling_window: ['stats'],
  before_after_milestone: ['stats'],
};

// kind → allowed picker granularities. Defaults to all five granularities;
// `period_over_period` drops '6M' and 'All' (a current-vs-prior comparison
// over an unbounded or half-year window is meaningless).
const PERIOD_OVER_PERIOD_WINDOWS: Granularity[] = ['W', 'M', 'Y'];

export const SUMMARY_KIND_WINDOWS: Record<SummaryKind, Granularity[]> = {
  single_feature_stats: [...GRANULARITIES],
  stratified_single: [...GRANULARITIES],
  streaks: [...GRANULARITIES],
  logging_rate: [...GRANULARITIES],
  period_over_period: PERIOD_OVER_PERIOD_WINDOWS,
  outliers: [...GRANULARITIES],
  project_narrative: [...GRANULARITIES],
  paired_same_day: [...GRANULARITIES],
  paired_lagged: [...GRANULARITIES],
  threshold_cross: [...GRANULARITIES],
  rolling_window: [...GRANULARITIES],
  before_after_milestone: [...GRANULARITIES],
};

// Whether THIS build knows how to render a config's kind at all.
//
// `kind` is a VARCHAR server-side and the read model passes unknown values
// through unchanged (see the note on `SummaryConfigRead.kind`), so a config
// written by a newer build — or by an offline backfill run from another clone
// against the shared database — legitimately reaches a surface whose registries
// have no entry for it. Rendering one throws on `SUMMARY_VIEWS[kind][0]`, and a
// throw inside the grid unmounts EVERY card, not just that one. Callers use
// this to skip what they can't draw, so an unknown kind costs its own card and
// nothing else.
export function isRenderableKind(kind: string): kind is SummaryKind {
  return Object.prototype.hasOwnProperty.call(SUMMARY_VIEWS, kind);
}

// Convenience helper mirroring `systemDefaultFor` in the chart registry:
// the default view for a kind is the first entry in its cycle.
export function defaultViewFor(kind: SummaryKind): SummaryViewId {
  return SUMMARY_VIEWS[kind][0];
}

// The views a specific CONFIG can render, given its source. A project-tag
// source (scope='project') has no per-feature value series, so views that need
// the raw feature timeseries (line / timeseries / distribution) can't render —
// drop them, keeping at least the first remaining view. Feature-sourced configs
// get the full cycle. This is why a tag `period_over_period` opens on 'stats'
// (its count comparison) rather than the empty 'line' chart.
export function viewsForConfig(config: SummaryConfig): SummaryViewId[] {
  const all = SUMMARY_VIEWS[config.kind];
  if (config.scope !== 'project') return all;
  const filtered = all.filter(
    (v) => !SUMMARY_VIEW_SOURCES[v].includes('timeseries'),
  );
  return filtered.length ? filtered : [all[0]];
}

// The granularity a card opens on. Most kinds start on the single-month window
// (the picker's default); streaks and logging_rate open on 'All' — a streak is
// an all-time arc, and a lifetime logging rate ("logged on X% of all days") is
// the most compelling framing and always a valid window (the sentinel-bounded
// 'all' period the rate builder anchors to the first active day).
export function defaultGranularityFor(kind: SummaryKind): Granularity {
  return kind === 'streaks' || kind === 'logging_rate' ? 'All' : 'M';
}

// Allowed picker granularities for a specific CONFIG, not just its kind. Starts
// from the kind's static `SUMMARY_KIND_WINDOWS` list, then narrows the two
// cadence-driven kinds (streaks, logging_rate) by cadence: a once-a-week cadence
// makes the 'W' (single-week) window a useless 1-cell strip, and a monthly
// cadence makes both 'W' and 'M' degenerate (0% or 100% out of one bucket).
// (Daily cadence and every other kind are unaffected.) The card uses this in
// place of the raw map so the period picker never offers a meaningless window.
export function windowsForConfig(config: SummaryConfig): Granularity[] {
  const base = SUMMARY_KIND_WINDOWS[config.kind];
  if (config.kind !== 'streaks' && config.kind !== 'logging_rate') return base;
  const cadence = (config.params as { cadence?: string } | null | undefined)?.cadence;
  if (cadence === 'monthly') return base.filter((g) => g !== 'W' && g !== 'M');
  if (cadence === 'weekly') return base.filter((g) => g !== 'W');
  return base;
}

// view id → body component. Mirrors `CHART_BODIES` in `charts/registry.tsx`:
// bodies are stateless visualizations (no card chrome, header, or picker);
// the card mounts one inside its chrome and drives the active window. The
// `Record<SummaryViewId, …>` shape makes the compiler flag any view id that
// lacks a body.
export const SUMMARY_VIEW_BODIES: Record<SummaryViewId, React.FC<SummaryViewBodyProps>> = {
  stats: StatsSummaryView,
  line: LineSummaryView,
  timeseries: TimeseriesSummaryView,
  distribution: DistributionSummaryView,
  calendar: CalendarSummaryView,
  'coverage-trend': CoverageTrendView,
};

// Switcher icon + label per view. These are SUMMARY-view icons, NOT the chart
// `CHART_ICONS` — the stats view is a current/prior/change *table*, so it gets a
// table-grid glyph rather than `CHART_ICONS.stats` (a bar chart, which wrongly
// implies switching to a bar graph). The line view reuses the chart line icon.
const TABLE_ICON: React.ReactNode = (
  <svg
    className="h-5 w-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth={2}
  >
    <rect x="3" y="4" width="18" height="16" rx="1.5" />
    <path d="M3 10h18M3 15h18M9 10v10M15 10v10" />
  </svg>
);

export const SUMMARY_VIEW_ICONS: Record<SummaryViewId, React.ReactNode> = {
  stats: TABLE_ICON,
  line: CHART_ICONS.line,
  // The trend is a line/area chart too; it reuses the line glyph. The two never
  // share a kind, so there's no in-card ambiguity — the LABELS disambiguate.
  timeseries: CHART_ICONS.line,
  distribution: CHART_ICONS.histogram,
  calendar: CHART_ICONS.activity,
  // A line chart, like the trend/line views — they never share a kind, so the
  // LABELS disambiguate.
  'coverage-trend': CHART_ICONS.line,
};

export const SUMMARY_VIEW_LABELS: Record<SummaryViewId, string> = {
  stats: 'Table',
  line: 'Chart',
  timeseries: 'Trend',
  distribution: 'Distribution',
  // The streaks visual: a daily month-heatmap, or — for weekly/monthly cadence —
  // the 1-D period strip. "Streak" reads right for both (the cadence is internal
  // to the body); see CalendarSummaryView / StreakStripView.
  calendar: 'Streak',
  'coverage-trend': 'Trend',
};

// Loading-skeleton shape per view. The card reserves roughly the loaded view's
// height while its data fetches, so switching to a chart view doesn't show a
// short stats-grid skeleton and then jump tall when the chart arrives. The
// chart bodies render in an h-64 box; 'stats' is the short metric grid.
export const SUMMARY_VIEW_SKELETON: Record<SummaryViewId, 'stats' | 'chart'> = {
  stats: 'stats',
  line: 'chart',
  timeseries: 'chart',
  distribution: 'chart',
  calendar: 'chart',
  'coverage-trend': 'chart',
};
